import React from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import Divider from "@material-ui/core/Divider";
import Box from "@material-ui/core/Box";
import Chip from "@material-ui/core/Chip";
import CircularProgress from "@material-ui/core/CircularProgress";
import TicketListItem from "../TicketListItemCustom";
import MoveToInboxIcon from "@material-ui/icons/MoveToInbox";
import ScheduleIcon from "@material-ui/icons/Schedule";
import LockIcon from "@material-ui/icons/Lock";
import { orange, red } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  panel: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.mode === 'dark' 
      ? '0 2px 8px rgba(0,0,0,0.3)' 
      : theme.shadows[2],
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    paddingBottom: theme.spacing(1),
    borderBottom: `2px solid ${theme.mode === 'dark' ? '#4B5563' : theme.palette.divider}`,
  },
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : 'transparent'}`,
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: theme.mode === 'dark' ? '#4B5563' : theme.palette.grey[100],
    },
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  sectionIcon: {
    fontSize: "1.2rem",
    marginRight: theme.spacing(0.5),
  },
  countChip: {
    marginLeft: theme.spacing(1),
    backgroundColor: theme.mode === 'dark' ? '#1e40af' : theme.palette.primary.main,
    color: 'white',
    fontWeight: 600,
  },
  emptyState: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontStyle: "italic",
  },
  emptySectionText: {
    padding: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    fontSize: "0.875rem",
  },
  ticketsList: {
    padding: 0,
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.primary,
  },
  divider: {
    margin: theme.spacing(2, 0),
    backgroundColor: theme.mode === 'dark' ? '#4B5563' : theme.palette.divider,
  },
  executionTimeChip: {
    marginLeft: theme.spacing(1),
    backgroundColor: theme.mode === 'dark' ? '#059669' : '#10b981',
    color: 'white',
    fontSize: "0.75rem",
  },
}));

const SearchResultsPanel = ({ 
  results, 
  searchParam, 
  loading, 
  onTicketClick 
}) => {
  const classes = useStyles();
  const theme = useTheme();

  if (loading) {
    return (
      <Paper className={classes.panel}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography variant="body2" style={{ marginLeft: 16 }}>
            {i18n.t("tickets.searchResults.searching")}
          </Typography>
        </Box>
      </Paper>
    );
  }

  if (!results) {
    return null;
  }

  const totalResults = (results.open?.count || 0) + 
                      (results.pending?.count || 0) + 
                      (results.closed?.count || 0);

  if (totalResults === 0) {
    return (
          <Paper className={classes.panel}>
            <Typography variant="h6" className={classes.header}>
              <span role="img" aria-label={i18n.t("tickets.searchResults.searchAria")}>🔍</span>{" "}
              {i18n.t("tickets.searchResults.resultsFor", { term: searchParam })}
            </Typography>
            <Box className={classes.emptyState}>
              <Typography variant="body1">
                {i18n.t("tickets.searchResults.noResults")}
              </Typography>
            </Box>
          </Paper>
    );
  }

  const renderSection = (title, icon, tickets, count, statusType) => {
    return (
      <Box className={classes.section}>
        <Box className={classes.sectionHeader}>
          <Typography className={classes.sectionTitle}>
            {icon}
            <span>{title}</span>
          </Typography>
          <Chip 
            label={`${count}`} 
            size="small" 
            className={classes.countChip}
            color="primary"
          />
        </Box>
        <Divider className={classes.divider} />
        {(!tickets || tickets.length === 0) ? (
          <Typography color="textSecondary" className={classes.emptySectionText}>
            {i18n.t("tickets.searchResults.emptySection")}
          </Typography>
        ) : (
          <List className={classes.ticketsList} dense>
            {tickets.map((ticket) => (
              <TicketListItem
                key={ticket.id}
                ticket={{
                  ...ticket,
                  uuid: ticket.uuid || ticket.id, // ✅ Usar uuid si existe, sino usar id
                  contact: {
                    id: ticket.contactId,
                    name: ticket.contactName,
                    number: ticket.contactNumber,
                    profilePicUrl: ticket.profilePicUrl,
                    isGroup: false
                  },
                  queue: ticket.queueId ? {
                    id: ticket.queueId,
                    name: ticket.queueName,
                    color: ticket.queueColor
                  } : null,
                  user: ticket.userId ? {
                    id: ticket.userId,
                    name: ticket.userName
                  } : null,
                  whatsapp: ticket.whatsappId ? {
                    id: ticket.whatsappId,
                    name: ticket.whatsappName
                  } : null
                }}
                onClick={() => onTicketClick && onTicketClick(ticket)}
                isGlobalSearch={true}
              />
            ))}
          </List>
        )}
      </Box>
    );
  };

  return (
    <Paper className={classes.panel} elevation={2}>
      <Box className={classes.header}>
        <Typography variant="h6">
          <span role="img" aria-label={i18n.t("tickets.searchResults.searchAria")}>🔍</span>{" "}
          {i18n.t("tickets.searchResults.resultsFor", { term: searchParam })}
        </Typography>
        <Chip 
          label={i18n.t("tickets.searchResults.total", { count: totalResults })} 
          color="primary" 
          size="small"
        />
      </Box>

      <Divider className={classes.divider} />

      {renderSection(
        i18n.t("tickets.searchResults.sections.open"),
        <MoveToInboxIcon className={classes.sectionIcon} style={{ color: theme.palette.primary.main }} />,
        results.open?.tickets || [],
        results.open?.count || 0,
        "open"
      )}

      <Divider className={classes.divider} />

      {renderSection(
        i18n.t("tickets.searchResults.sections.pending"),
        <ScheduleIcon className={classes.sectionIcon} style={{ color: orange[500] }} />,
        results.pending?.tickets || [],
        results.pending?.count || 0,
        "pending"
      )}

      <Divider className={classes.divider} />

      {renderSection(
        i18n.t("tickets.searchResults.sections.closed"),
        <LockIcon className={classes.sectionIcon} style={{ color: red[500] }} />,
        results.closed?.tickets || [],
        results.closed?.count || 0,
        "closed"
      )}
    </Paper>
  );
};

export default SearchResultsPanel;
